import { ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { useT } from "@/i18n/provider";
import { Button } from "./button";
import { EmptyState } from "./card";
import { cx } from "./cx";
import { Skeleton } from "./spinner";

/**
 * A list (guide 3.8): a table on a computer or tablet (768 px and wider),
 * one card per item on a phone, like the phone app's rows.
 *
 *   - Clicking anywhere on a row opens the item; the first cell holds a real
 *     link, so Tab and Enter open it too, and a middle click opens a new tab.
 *   - Money and numbers are right-aligned with figures that line up.
 *   - Loading: 5 grey rows. Nothing: the phone's words. More on the server:
 *     a Load more button (no numbered pages).
 */

export interface Column<T> {
  /** Header text, from i18n. */
  header: string;
  cell: (row: T) => React.ReactNode;
  /** Numbers and money: right-aligned, tabular figures. */
  numeric?: boolean;
  /** A fixed or minimum width, as a Tailwind class: "w-40". */
  className?: string;
}

export interface DataListProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  /** Where a row goes when clicked. Without it, rows are not links. */
  rowHref?: (row: T) => string;
  /** The row on phone width. Defaults to the columns stacked. */
  renderCard?: (row: T) => React.ReactNode;
  /** A short name for the table, for screen readers. */
  label: string;
  loading?: boolean;
  /** Shown when there are no rows (and not loading). */
  empty: string;
  emptyAction?: React.ReactNode;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

export function DataList<T>(props: DataListProps<T>) {
  const t = useT();
  const { rows, loading, empty, emptyAction, hasMore, loadingMore, onLoadMore } = props;

  if (!loading && rows && rows.length === 0) {
    return (
      <div className="rounded-card border border-border bg-surface">
        <EmptyState message={empty} action={emptyAction} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="hidden md:block">
        <Table {...props} />
      </div>
      <div className="md:hidden">
        <Cards {...props} />
      </div>
      {hasMore && onLoadMore && (
        <div className="flex justify-center">
          <Button variant="secondary" label={t.loadMore} loading={loadingMore} onClick={onLoadMore} />
        </div>
      )}
    </div>
  );
}

function Table<T>({ columns, rows, rowKey, rowHref, label, loading }: DataListProps<T>) {
  const navigate = useNavigate();
  const align = (c: Column<T>) => (c.numeric ? "text-right tabular-nums" : "text-left");

  return (
    <div className="overflow-x-auto rounded-card border border-border bg-surface">
      <table className="w-full border-collapse" aria-label={label} aria-busy={loading || undefined}>
        <thead className="sticky top-0 z-10 bg-surface">
          <tr className="border-b border-border">
            {columns.map((c) => (
              <th
                key={c.header}
                scope="col"
                className={cx("px-4 py-3 text-small font-semibold whitespace-nowrap text-muted", align(c), c.className)}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && !rows
            ? Array.from({ length: 5 }, (_, i) => (
                <tr key={i} className="h-[52px] border-b border-border last:border-b-0">
                  {columns.map((c) => (
                    <td key={c.header} className="px-4">
                      <Skeleton className={cx("h-4", c.numeric ? "ml-auto w-16" : "w-3/4")} />
                    </td>
                  ))}
                </tr>
              ))
            : rows?.map((row) => {
                const href = rowHref?.(row);
                return (
                  <tr
                    key={rowKey(row)}
                    onClick={(e) => {
                      // A click on a button or link inside the row does its own job.
                      if (!href || (e.target as HTMLElement).closest("a, button, input, label")) return;
                      void navigate(href);
                    }}
                    className={cx(
                      "h-[52px] border-b border-border text-base text-text last:border-b-0",
                      href && "cursor-pointer hover:bg-primary-soft",
                    )}
                  >
                    {columns.map((c, i) => (
                      <td key={c.header} className={cx("px-4 py-2", align(c), c.className)}>
                        {i === 0 && href ? (
                          <Link to={href} className="outline-offset-4 hover:underline">
                            {c.cell(row)}
                          </Link>
                        ) : (
                          c.cell(row)
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
        </tbody>
      </table>
    </div>
  );
}

function Cards<T>({ columns, rows, rowKey, rowHref, renderCard, label, loading }: DataListProps<T>) {
  if (loading && !rows) {
    return (
      <ul aria-label={label} aria-busy="true" className="flex flex-col gap-2">
        {Array.from({ length: 5 }, (_, i) => (
          <li key={i} className="flex flex-col gap-2 rounded-card bg-surface p-4 shadow-card">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul aria-label={label} className="flex flex-col gap-2">
      {rows?.map((row) => {
        const href = rowHref?.(row);
        const body = renderCard ? (
          renderCard(row)
        ) : (
          <div className="flex flex-col gap-1">
            {columns.map((c) => (
              <div key={c.header} className="flex justify-between gap-3">
                <span className="text-small text-muted">{c.header}</span>
                <span className={cx("text-base text-text", c.numeric && "tabular-nums")}>{c.cell(row)}</span>
              </div>
            ))}
          </div>
        );
        const box = "flex items-center gap-3 rounded-card bg-surface p-4 shadow-card dark:border dark:border-border";
        return (
          <li key={rowKey(row)}>
            {href ? (
              <Link to={href} className={cx(box, "hover:bg-primary-soft")}>
                <div className="min-w-0 flex-1">{body}</div>
                <ChevronRight size={20} aria-hidden="true" className="shrink-0 text-muted" />
              </Link>
            ) : (
              <div className={box}>
                <div className="min-w-0 flex-1">{body}</div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
